import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  Chip,
  Divider,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  alpha,
  IconButton,
  Skeleton,
  useTheme,
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  RocketLaunch as ActivateIcon,
  Paid as PaidIcon,
  Autorenew as RenewIcon,
  Undo as UndoIcon,
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  ReceiptLong as ReceiptLongIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  formatBillingCurrency,
  formatDashedDateWithYear,
} from "../../../utils/hooks/dataHandler";
import BillingHistorySection from "./BillingHistorySection";
import ConfirmationDialog from "../../utils/dialog/confirmationDialog";
import BaseDialog from "../../utils/dialog/BaseDialog";

const formatPaymentMethod = (method) => {
  if (!method) return "—";
  const type = method || "unknown";

  switch (type.toLowerCase()) {
    case "card":
      return "Card";
    case "bank_transfer":
      return "Bank Transfer";
    case "offline_transfer":
      return "Offline Transfer";
    default:
      return String(type)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

const STATUS_CHIP = {
  active: { label: "Active", color: "success" },
  trialing: { label: "Trialing", color: "primary" },
  past_due: { label: "Payment overdue", color: "error" },
  cancelled: { label: "Cancelled", color: "default" },
  expired: { label: "Expired", color: "default" },
};

/** Matches `BillingHistorySection` table header cells. */
const BILLING_TABLE_HEAD_CELL_SX = {
  fontWeight: 700,
  color: "text.primary",
  backgroundColor: "background.default",
  borderBottom: 2,
  borderColor: "divider",
};

const PAYMENT_HISTORY_STATUS_MAP = {
  succeeded: { label: "Succeeded", color: "success" },
  paid: { label: "Paid", color: "success" },
  failed: { label: "Failed", color: "error" },
  pending: { label: "Pending", color: "info" },
  refunded: { label: "Refunded", color: "warning" },
};

/** Same chip treatment as `BillingHistorySection` `StatusBadge`. */
function PaymentHistoryStatusBadge({ status }) {
  const key = (status || "").toLowerCase();
  const map = PAYMENT_HISTORY_STATUS_MAP[key] || {
    label: status ? String(status).replace(/_/g, " ") : "Unknown",
    color: "default",
  };

  return (
    <Chip
      label={map.label}
      size='small'
      sx={{
        fontWeight: 600,
        textTransform: "capitalize",
        bgcolor: (theme) =>
          map.color === "default"
            ? theme.palette.grey[100]
            : theme.palette[map.color]?.main ||
              theme.palette.background.default,
        color: (theme) =>
          map.color === "default"
            ? theme.palette.text.primary
            : theme.palette[map.color]?.contrastText ||
              theme.palette.text.primary,
        borderColor: (theme) =>
          map.color !== "default"
            ? alpha(
                theme.palette[map.color]?.main || theme.palette.divider,
                0.3,
              )
            : "divider",
        "& .MuiChip-icon": {
          mr: 0.5,
        },
      }}
    />
  );
}

function PaymentHistoryEmptyState() {
  return (
    <Paper
      variant='outlined'
      sx={{
        p: 4,
        borderRadius: 3,
        borderStyle: "dashed",
        borderColor: "divider",
        textAlign: "center",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          backgroundColor: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2,
        }}
      >
        <ReceiptLongIcon sx={{ fontSize: 32, color: "text.secondary" }} />
      </Box>
      <Typography variant='h6' fontWeight={600} color='text.primary' mb={1}>
        No Payment History
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        Completed charges will appear here after you load payment history.
      </Typography>
    </Paper>
  );
}

function PaymentHistoryLoadingSkeleton() {
  return (
    <Paper variant='outlined' sx={{ borderRadius: 3, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton width='70%' />
                </TableCell>
                <TableCell align='right'>
                  <Skeleton width={80} sx={{ ml: "auto" }} />
                </TableCell>
                <TableCell align='center'>
                  <Skeleton width={56} sx={{ mx: "auto" }} />
                </TableCell>
                <TableCell align='center'>
                  <Skeleton
                    width={72}
                    height={24}
                    sx={{ borderRadius: 1, mx: "auto" }}
                  />
                </TableCell>
                <TableCell align='center'>
                  <Skeleton width={48} sx={{ mx: "auto" }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function InfoRow({ label, children, dense }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={0.5}
      justifyContent='space-between'
      alignItems={{ xs: "flex-start", sm: "center" }}
      sx={{ py: dense ? 0.5 : 0.75 }}
    >
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ flexShrink: 0, fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Box sx={{ textAlign: { xs: "left", sm: "right" }, minWidth: 0 }}>
        {children}
      </Box>
    </Stack>
  );
}

const TenantBillingOverview = ({
  billingData = {},
  subscriptionStat = {},
  onViewPlans,
  onActivateSubscription,
  onConfirmPayment,
  onTerminateSubscription,
  revertPendingPlanRequest,
  reactivateSubscriptionRequest,
  activating,
  charging,
  terminating,
  paymentHistory,
  paymentHistoryLoading,
  paymentHistoryLoaded,
  onLoadPaymentHistory,
}) => {
  const theme = useTheme();
  const [dialog, setDialog] = useState(null);
  const [showUnpaidLog, setShowUnpaidLog] = useState(false);

  const {
    status: billingRowStatus,
    verified,
    isTrial,
    isPending,
    trial_end: billingTrialEnd,
    next_billing_date,
    next_payment_cents,
    payment_after_discount,
    outstanding_amount_cents,
    upcoming_recurring_amount_cents,
    discount: billingDiscount,
    plan_url,
    user_count,
    billable_user_count,
    company_count,
    log = [],
  } = billingData;

  const {
    subscription_status,
    plan,
    next_period_plan,
    pending_plan_id,
    renewal,
    countdown_days,
    countdown_to,
    countdown_type,
    next_subscription_cycle_start_date,
    trial_end: statusTrialEnd,
    active_company_count,
    inactive_company_count,
    subuser_count,
    subsidiary_count,
    effective_discount_percent,
    discount_rate_percent,
    hitpay_payment_method_verified,
  } = subscriptionStat;

  const trialEnd = statusTrialEnd ?? billingTrialEnd;
  const rawStatus = subscription_status || billingRowStatus || "";
  const statusMeta = STATUS_CHIP[rawStatus] || {
    label: rawStatus ? String(rawStatus).replace(/_/g, " ") : "Unknown",
    color: "default",
  };

  const amountDueCents =
    payment_after_discount ??
    outstanding_amount_cents ??
    next_payment_cents ??
    0;

  const hasOutstanding =
    typeof amountDueCents === "number" && amountDueCents > 0;
  const showActivate =
    verified === false || hitpay_payment_method_verified === false;

  const countdownLabel =
    countdown_type === "trial_end"
      ? "trial ends"
      : countdown_type === "next_billing"
        ? "next billing"
        : "next milestone";

  const discountPct =
    effective_discount_percent ?? discount_rate_percent ?? billingDiscount ?? 0;

  const primaryBusy = activating || charging || terminating;

  return (
    <Stack spacing={3}>
      {/* Title */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent='space-between'
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
      >
        <Typography variant='h5' fontWeight={800} letterSpacing={-0.5}>
          Billing & subscription
        </Typography>
        <Button
          variant='outlined'
          onClick={onViewPlans}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Browse plans
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
          background: (theme) =>
            `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.paper} 60%)`,
          boxShadow: (theme) =>
            `0 16px 40px ${alpha(theme.palette.primary.main, 0.08)}, 0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            boxShadow: (theme) =>
              `0 20px 48px ${alpha(theme.palette.primary.main, 0.12)}, 0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
            transform: "translateY(-2px)",
          },
        }}
      >
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems='stretch'>
          <Grid item xs={12} md={7}>
            <Stack spacing={2.5}>
              <Stack
                direction='row'
                alignItems='center'
                flexWrap='wrap'
                gap={1.25}
              >
                {/* Status & renewal chips, update according to renewal state */}
                {renewal === false ? (
                  <>
                    <Chip
                      label='Terminated'
                      size='medium'
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        px: 2,
                        borderColor: (theme) => theme.palette.divider,
                        borderRadius: "1rem",
                        boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
                        height: 36,
                        bgcolor: "#FFF0F0",
                        color: (theme) => theme.palette.error.main,
                      }}
                    />
                    <Chip
                      label='Renewal paused'
                      variant='outlined'
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        px: 2,
                        borderRadius: "1rem",
                        height: 36,
                        boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
                        bgcolor: "#FFF8E1",
                        color: (theme) => theme.palette.warning.dark,
                        borderColor: (theme) => theme.palette.divider,
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Chip
                      label={statusMeta.label}
                      size='medium'
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        px: 2,
                        borderColor: (theme) => theme.palette.divider,
                        borderRadius: "1rem",
                        boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
                        height: 36,
                        bgcolor: (theme) =>
                          statusMeta.color === "success"
                            ? "#E8F5E9"
                            : statusMeta.color === "primary"
                              ? "#E3F2FD"
                              : statusMeta.color === "warning"
                                ? "#FFF8E1"
                                : statusMeta.color === "error"
                                  ? "#FFF0F0"
                                  : theme.palette.background.paper,
                        color: (theme) =>
                          statusMeta.color === "success"
                            ? theme.palette.success.dark
                            : statusMeta.color === "primary"
                              ? theme.palette.primary.main
                              : statusMeta.color === "warning"
                                ? theme.palette.warning.dark
                                : statusMeta.color === "error"
                                  ? theme.palette.error.main
                                  : theme.palette.text.primary,
                      }}
                    />
                    <Chip
                      label='Auto-renew on'
                      variant='outlined'
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        px: 2,
                        borderRadius: "1rem",
                        height: 36,
                        boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
                        bgcolor: "#E8F5E9",
                        color: (theme) => theme.palette.success.dark,
                        borderColor: (theme) => theme.palette.divider,
                      }}
                    />
                  </>
                )}
                {/* Card/status chips, but do not show 'activate subscription' button if terminated */}
                {renewal !== false &&
                  (verified === true ||
                    hitpay_payment_method_verified === true) && (
                    <Chip
                      label='Card verified'
                      variant='outlined'
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        px: 2,
                        borderRadius: "1rem",
                        height: 32,
                        boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
                        bgcolor: "#E8F5E9",
                        color: (theme) => theme.palette.success.dark,
                        borderColor: (theme) => theme.palette.divider,
                      }}
                    />
                  )}
                {renewal !== false && isPending && (
                  <Chip
                    label='Setup pending'
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      px: 2,
                      borderRadius: "1rem",
                      height: 36,
                      boxShadow: "0px 1px 2px rgba(0,0,0,0.08)",
                      bgcolor: "#FFF8E1",
                      color: (theme) => theme.palette.warning.dark,
                      border: "none",
                    }}
                  />
                )}
              </Stack>

              <Box>
                <Typography
                  variant='overline'
                  fontWeight={700}
                  color='text.secondary'
                  letterSpacing={1.2}
                  display='block'
                  gutterBottom
                >
                  {renewal === false
                    ? "Subscription will terminate at end of current period"
                    : `Time until ${countdownLabel}`}
                </Typography>
                {renewal === false ? (
                  <>
                    <Typography
                      component='span'
                      sx={{
                        fontSize: { xs: "1.1rem", sm: "1.25rem" },
                        fontWeight: 600,
                        lineHeight: 1.4,
                        color: "primary.main",
                        letterSpacing: -0.5,
                        opacity: 0.9,
                      }}
                    >
                      This subscription will not renew. Access continues until
                      current period ends.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Stack
                      direction='row'
                      alignItems='baseline'
                      flexWrap='wrap'
                      gap={1}
                    >
                      <Typography
                        component='span'
                        sx={{
                          fontSize: { xs: "2.75rem", sm: "3.5rem", md: "4rem" },
                          fontWeight: 800,
                          lineHeight: 1.05,
                          color:
                            rawStatus === "past_due"
                              ? "error.main"
                              : "primary.main",
                          letterSpacing: -2,
                        }}
                      >
                        {countdown_days != null
                          ? Math.max(0, countdown_days)
                          : "—"}
                      </Typography>
                      {countdown_days != null && (
                        <Typography
                          component='span'
                          variant='h4'
                          fontWeight={700}
                          color={
                            rawStatus === "past_due"
                              ? "error.dark"
                              : "text.secondary"
                          }
                          sx={{ mb: 0.5 }}
                        >
                          days
                        </Typography>
                      )}
                    </Stack>
                    {countdown_to && (
                      <Typography
                        variant='body1'
                        color={
                          rawStatus === "past_due"
                            ? "error.main"
                            : "text.secondary"
                        }
                        fontWeight={500}
                        sx={{ mt: 0.5 }}
                      >
                        {rawStatus === "past_due"
                          ? " Your subscription payment is due. Please complete payment to maintain active service."
                          : formatDashedDateWithYear(countdown_to)}
                      </Typography>
                    )}
                    {next_subscription_cycle_start_date && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 1 }}
                      >
                        <CalendarIcon
                          sx={{
                            fontSize: 16,
                            verticalAlign: "text-bottom",
                            mr: 0.5,
                          }}
                        />
                        Next Cycle starts{" "}
                        {formatDashedDateWithYear(
                          next_subscription_cycle_start_date,
                        )}
                      </Typography>
                    )}
                    {trialEnd && countdown_type !== "trial_end" && (
                      <Typography
                        variant='caption'
                        display='block'
                        color='text.secondary'
                        sx={{ mt: 0.5 }}
                      >
                        Trial ends {formatDashedDateWithYear(trialEnd)}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack
              spacing={2}
              sx={{
                height: "100%",
                justifyContent: "space-between",
                alignItems: { xs: "stretch", md: "flex-end" },
                textAlign: { xs: "left", md: "right" },
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant='overline'
                  fontWeight={700}
                  color='text.secondary'
                  letterSpacing={1.2}
                  display='block'
                >
                  Amount due (after discount)
                </Typography>
                <Typography
                  variant='h3'
                  component='p'
                  fontWeight={800}
                  sx={{
                    fontSize: { xs: "2.25rem", sm: "2.75rem" },
                    lineHeight: 1.15,
                    color: hasOutstanding ? "error.main" : "success.main",
                    mt: 0.5,
                  }}
                >
                  {formatBillingCurrency(amountDueCents)}
                </Typography>
                <Typography
                  variant='body2'
                  fontWeight={600}
                  color={hasOutstanding ? "error.dark" : "success.dark"}
                  sx={{ mt: 0.5 }}
                >
                  {hasOutstanding
                    ? "You have a balance to take care of"
                    : "No outstanding balance"}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                sx={{
                  width: "100%",
                  justifyContent: { xs: "stretch", md: "flex-end" },
                }}
              >
                {/* Only show pay/update options if subscription not terminated */}
                {renewal !== false && !showActivate && (
                  <Button
                    variant='contained'
                    size='large'
                    fullWidth={false}
                    startIcon={
                      charging ? (
                        <CircularProgress size={18} color='inherit' />
                      ) : (
                        <PaidIcon />
                      )
                    }
                    disabled={primaryBusy}
                    onClick={() => setDialog("pay")}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.25,
                      boxShadow: 3,
                      backgroundColor: hasOutstanding
                        ? "error.main"
                        : "success.main",
                      "&:hover": {
                        backgroundColor: hasOutstanding
                          ? "error.dark"
                          : "success.dark",
                        boxShadow: 6,
                      },
                    }}
                  >
                    {charging ? "Paying..." : "Pay now"}
                  </Button>
                )}
                {renewal !== false && !showActivate && plan_url && (
                  <Button
                    variant={hasOutstanding ? "outlined" : "contained"}
                    color='secondary'
                    size='large'
                    startIcon={<CreditCardIcon />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
                    href={plan_url}
                    rel='noopener noreferrer'
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.25,
                    }}
                  >
                    Update payment method
                  </Button>
                )}
                {renewal !== false && showActivate && (
                  <Tooltip title='Redirect to HitPay to add or verify your card'>
                    <span>
                      <Button
                        variant='contained'
                        color='primary'
                        size='large'
                        startIcon={<ActivateIcon />}
                        disabled={primaryBusy}
                        onClick={() => setDialog("activate")}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 3,
                          py: 1.25,
                          boxShadow: 3,
                        }}
                      >
                        Activate subscription
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* —— Plan & usage | Billing snapshot —— */}
      <Grid container alignItems='stretch'>
        <Grid item xs={12} lg={7}>
          <Paper
            variant='outlined'
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              height: "100%",
              borderColor: "divider",
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ mb: 1 }}
              spacing={1}
            >
              <Typography
                variant='overline'
                fontWeight={800}
                color='primary.main'
                letterSpacing={1}
                gutterBottom
                display='block'
              >
                Plan & usage
              </Typography>
              <IconButton
                size='small'
                color='primary'
                aria-label='View unpaid usage'
                sx={{
                  mb: "2px",
                }}
                onClick={() => setShowUnpaidLog((prev) => !prev)}
              >
                <Tooltip title='View unpaid usage'>
                  <HistoryIcon />
                </Tooltip>
              </IconButton>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                >
                  Current plan
                </Typography>
                <Typography variant='h6' fontWeight={800} sx={{ mt: 0.5 }}>
                  {plan?.name || "—"}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 0.5 }}
                >
                  {plan?.price_cents_per_user != null
                    ? `${formatBillingCurrency(plan.price_cents_per_user)} / user / ${plan?.billing_interval || "cycle"}`
                    : ""}
                </Typography>
                {plan?.extra_company_fee_cents != null && (
                  <Typography variant='caption' display='block' sx={{ mt: 1 }}>
                    Extra company fee:{" "}
                    {formatBillingCurrency(plan.extra_company_fee_cents)}
                  </Typography>
                )}
              </Grid>
              {pending_plan_id && next_period_plan?.id !== plan?.id && (
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    fontWeight={600}
                  >
                    Next billing period
                  </Typography>
                  <Typography variant='h6' fontWeight={800} sx={{ mt: 0.5 }}>
                    {next_period_plan?.name || plan?.name || "—"}
                  </Typography>

                  <Chip
                    sx={{ mt: 1.5, fontWeight: 700 }}
                    label='Change scheduled'
                    color='info'
                    size='small'
                  />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography
              variant='subtitle2'
              fontWeight={700}
              color='text.secondary'
              gutterBottom
            >
              Usage
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Typography variant='caption' color='text.secondary'>
                  Companies
                </Typography>
                <Typography variant='h6' fontWeight={800}>
                  {active_company_count ?? company_count ?? "—"}
                  {inactive_company_count != null ? (
                    <Typography
                      component='span'
                      variant='body2'
                      color='text.secondary'
                      fontWeight={500}
                    >
                      {" "}
                      / {inactive_company_count} inactive
                    </Typography>
                  ) : null}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant='caption' color='text.secondary'>
                  Sub-users
                </Typography>
                <Typography variant='h6' fontWeight={800}>
                  {subuser_count ?? user_count ?? "—"}
                  {billable_user_count != null ? (
                    <Tooltip title='Includes account owner'>
                      <Typography
                        component='span'
                        variant='body2'
                        color='text.secondary'
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          ml: 1,
                          cursor: "default",
                        }}
                      >
                        ({billable_user_count} billable)
                      </Typography>
                    </Tooltip>
                  ) : null}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant='caption' color='text.secondary'>
                  Subsidiaries
                </Typography>
                <Typography variant='h6' fontWeight={800}>
                  {subsidiary_count ?? "—"}
                </Typography>
              </Grid>
            </Grid>

            {pending_plan_id && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant='outlined'
                  size='medium'
                  startIcon={<UndoIcon />}
                  disabled={primaryBusy}
                  onClick={() => revertPendingPlanRequest?.()}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Cancel scheduled plan change
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            variant='outlined'
            sx={{
              ml: 1,
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              height: "100%",
              bgcolor: (t) => alpha(t.palette.grey[500], 0.04),
            }}
          >
            <Typography
              variant='overline'
              fontWeight={800}
              color='text.secondary'
              letterSpacing={1}
              gutterBottom
              display='block'
            >
              Billing snapshot
            </Typography>
            <Typography variant='h6' fontWeight={800} sx={{ mb: 2 }}>
              Schedule & rates
            </Typography>

            <Stack spacing={1.25}>
              <InfoRow label='Next billing date' dense>
                <Typography fontWeight={700}>
                  {renewal && next_billing_date
                    ? formatDashedDateWithYear(next_billing_date)
                    : "—"}
                </Typography>
              </InfoRow>
              {renewal === true && (
                <>
                  <Divider />
                  <InfoRow label='Upcoming recurring (preview)' dense>
                    <Typography fontWeight={800} color='primary.main'>
                      {formatBillingCurrency(
                        upcoming_recurring_amount_cents ?? 0,
                      )}
                    </Typography>
                  </InfoRow>
                  <InfoRow label='Your discount' dense>
                    <Typography fontWeight={700}>{discountPct}%</Typography>
                  </InfoRow>
                </>
              )}
            </Stack>

            <Divider sx={{ my: 3 }} />

            <Typography variant='subtitle2' fontWeight={800} gutterBottom>
              Other actions
            </Typography>
            <Stack spacing={1.25}>
              {renewal === false && (
                <Button
                  variant='outlined'
                  color='success'
                  fullWidth
                  startIcon={<RenewIcon />}
                  onClick={() => reactivateSubscriptionRequest?.()}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Restore renewal
                </Button>
              )}
              {renewal === true && (
                <Button
                  variant='text'
                  color='error'
                  fullWidth
                  startIcon={<StopIcon />}
                  onClick={() => setDialog("terminate")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    border: (theme) =>
                      `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                  }}
                >
                  End subscription after this period
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment history */}
      <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={2}
        >
          <Typography variant='h6' fontWeight={800}>
            Payment history
          </Typography>
          {paymentHistoryLoaded ? (
            <Tooltip title='Refresh payment history'>
              <span>
                <IconButton
                  color='primary'
                  onClick={onLoadPaymentHistory}
                  disabled={paymentHistoryLoading}
                  sx={{ fontWeight: 700 }}
                  size='medium'
                >
                  {paymentHistoryLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant='outlined'
              size='medium'
              startIcon={
                paymentHistoryLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <HistoryIcon />
                )
              }
              disabled={paymentHistoryLoading}
              onClick={onLoadPaymentHistory}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Load payment history
            </Button>
          )}
        </Stack>

        {paymentHistoryLoaded && (
          <>
            <Divider sx={{ my: 2 }} />
            {paymentHistoryLoading ? (
              <PaymentHistoryLoadingSkeleton />
            ) : paymentHistory?.length === 0 ? (
              <PaymentHistoryEmptyState />
            ) : (
              <Paper
                variant='outlined'
                sx={{
                  borderRadius: 3,
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table
                    stickyHeader
                    size='medium'
                    aria-label='payment history table'
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={BILLING_TABLE_HEAD_CELL_SX}>
                          Paid at
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={BILLING_TABLE_HEAD_CELL_SX}
                        >
                          Amount
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={BILLING_TABLE_HEAD_CELL_SX}
                        >
                          Method
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={BILLING_TABLE_HEAD_CELL_SX}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={BILLING_TABLE_HEAD_CELL_SX}
                        >
                          Receipt
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistory.map((row, index) => (
                        <TableRow
                          key={row.id || index}
                          hover
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.02,
                              ),
                            },
                            transition:
                              theme.transitions.create("background-color"),
                          }}
                        >
                          <TableCell
                            align='left'
                            sx={{ py: 2.5, color: "text.secondary" }}
                          >
                            {row.paid_at
                              ? formatDashedDateWithYear(row.paid_at)
                              : "—"}
                          </TableCell>
                          <TableCell align='right' sx={{ py: 2.5 }}>
                            <Typography variant='subtitle2' fontWeight={600}>
                              {formatBillingCurrency(
                                row.amount_cents,
                                (row.currency || "sgd").toUpperCase(),
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align='center' sx={{ py: 2.5 }}>
                            <Typography
                              variant='subtitle2'
                              fontWeight={600}
                              sx={{ textTransform: "capitalize" }}
                            >
                              {formatPaymentMethod(row.payment_method)}
                            </Typography>
                          </TableCell>
                          <TableCell align='center' sx={{ py: 2.5 }}>
                            <PaymentHistoryStatusBadge status={row.status} />
                          </TableCell>
                          <TableCell align='center' sx={{ py: 2.5 }}>
                            {row.receipt_url ? (
                              <Link
                                href={row.receipt_url}
                                target='_blank'
                                rel='noopener noreferrer'
                                underline='hover'
                                fontWeight={600}
                              >
                                View
                              </Link>
                            ) : (
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    backgroundColor: "grey.50",
                    borderTop: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    Showing {paymentHistory.length}{" "}
                    {paymentHistory.length === 1 ? "payment" : "payments"}
                  </Typography>
                </Box>
              </Paper>
            )}
          </>
        )}
      </Paper>

      <BaseDialog
        title='Unpaid Usage'
        open={showUnpaidLog}
        onClose={() => setShowUnpaidLog(false)}
        maxWidth='md'
        fullWidth
      >
        <BillingHistorySection
          billingHistory={log}
          next_billing_date={next_billing_date}
        />
      </BaseDialog>

      <ConfirmationDialog
        open={dialog === "pay"}
        title='Confirm payment'
        description='Charge the outstanding amount using your saved payment method?'
        onClose={() => setDialog(null)}
        onConfirm={async () => {
          setDialog(null);
          await onConfirmPayment?.();
        }}
      />
      <ConfirmationDialog
        open={dialog === "activate"}
        title='Activate subscription'
        description='You will be redirected to HitPay to add or verify your payment method.'
        onClose={() => setDialog(null)}
        onConfirm={async () => {
          setDialog(null);
          await onActivateSubscription?.();
        }}
      />
      <ConfirmationDialog
        open={dialog === "terminate"}
        title='Turn off renewal'
        description='Your subscription will stay active until the end of the current billing period. This does not immediately cancel access or settle unpaid adjustments.'
        confirmLabel='Continue'
        confirmColor='error'
        onClose={() => setDialog(null)}
        onConfirm={async () => {
          setDialog(null);
          await onTerminateSubscription?.();
        }}
      />
    </Stack>
  );
};

export default TenantBillingOverview;
